<template>
  <header class="app-header">
    <div class="header-container">
      <!-- Top Navigation Bar -->
      <div class="top-nav-bar">
        <div class="contact-info">
          <a href="mailto:theplayersleaguedfw@gmail.com" class="contact-item">
            <Icon name="mdi:email" />
            <span>theplayersleaguedfw@gmail.com</span>
          </a>
          <span class="divider">|</span>
          <span class="established">EST. 2025</span>
        </div>
        <div class="social-links">
          <a href="https://www.facebook.com/groups/1491189844590894" target="_blank" class="social-link">
            <Icon name="mdi:facebook" />
          </a>
        </div>
      </div>

      <!-- Main Header with Logo and Navigation -->
      <div class="main-header-content">
        <div class="logo-section">
          <img src="/logo-with-no-background.png" alt="The Players League DFW" class="logo-image" />
          <div class="brand-text">
            <h1 class="brand-name">The Players League DFW</h1>
            <p class="brand-tagline">Dallas-Fort Worth Premier Pool League</p>
            <div class="brand-accent">Bad Boys Tru-Team Format</div>
          </div>
        </div>

        <!-- Primary Navigation -->
        <nav class="primary-nav">
          <ul class="nav-menu">
            <li class="nav-item">
              <NuxtLink to="/" class="nav-link">
                <Icon name="mdi:home-outline" />
                <span>Home</span>
              </NuxtLink>
            </li>
            <li class="nav-item">
              <NuxtLink to="/about" class="nav-link">
                <Icon name="mdi:information-outline" />
                <span>About</span>
              </NuxtLink>
            </li>
            <li class="nav-item">
              <NuxtLink to="/league-format" class="nav-link">
                <Icon name="mdi:trophy-outline" />
                <span>League Format</span>
              </NuxtLink>
            </li>
            <li class="nav-item">
              <NuxtLink to="/standings" class="nav-link">
                <Icon name="mdi:chart-line" />
                <span>Standings</span>
              </NuxtLink>
            </li>
            <li class="nav-item">
              <NuxtLink to="/matches" class="nav-link">
                <Icon name="mdi:trophy-variant" />
                <span>Matches</span>
              </NuxtLink>
            </li>
            <li class="nav-item">
              <NuxtLink to="/bylaws" class="nav-link">
                <Icon name="mdi:file-document-outline" />
                <span>Bylaws</span>
              </NuxtLink>
            </li>
            <li class="nav-item">
              <NuxtLink to="/contact" class="nav-link">
                <Icon name="mdi:phone-outline" />
                <span>Contact</span>
              </NuxtLink>
            </li>
          </ul>

          <!-- Mobile Menu Toggle -->
          <button class="mobile-toggle" @click="toggleMobileMenu" :class="{ active: mobileMenuOpen }">
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
        </nav>
      </div>

      <!-- Mobile Navigation Menu -->
      <div class="mobile-nav-menu" :class="{ open: mobileMenuOpen }">
        <ul class="mobile-nav-list">
          <li><NuxtLink to="/" @click="closeMobileMenu" class="mobile-nav-link">Home</NuxtLink></li>
          <li><NuxtLink to="/about" @click="closeMobileMenu" class="mobile-nav-link">About</NuxtLink></li>
          <li><NuxtLink to="/league-format" @click="closeMobileMenu" class="mobile-nav-link">League Format</NuxtLink></li>
          <li><NuxtLink to="/standings" @click="closeMobileMenu" class="mobile-nav-link">Standings</NuxtLink></li>
          <li><NuxtLink to="/matches" @click="closeMobileMenu" class="mobile-nav-link">Matches</NuxtLink></li>
          <li><NuxtLink to="/bylaws" @click="closeMobileMenu" class="mobile-nav-link">Bylaws</NuxtLink></li>
          <li><NuxtLink to="/contact" @click="closeMobileMenu" class="mobile-nav-link">Contact</NuxtLink></li>
        </ul>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref } from 'vue'

const mobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}
</script>

<style scoped>
/* Header Container */
.app-header {
  background: linear-gradient(135deg, var(--navy-blue) 0%, var(--dark-blue) 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: sticky;
  top: 0;
  z-index: 1000;
  overflow-x: hidden;
  width: 100%;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  overflow-x: hidden;
  width: 100%;
}

/* Top Navigation Bar */
.top-nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 184, 28, 0.2);
  font-size: 0.9rem;
}

.contact-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--cream);
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-item:hover {
  color: var(--gold);
}

.divider {
  color: var(--gold);
  opacity: 0.5;
}

.established {
  color: var(--gold);
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.social-links {
  display: flex;
  gap: 12px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--cream);
  background: rgba(255, 184, 28, 0.1);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.social-link:hover {
  background: var(--gold);
  color: var(--navy-blue);
  transform: translateY(-2px);
}

/* Main Header Content */
.main-header-content {
  padding: 24px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
}

/* Professional Logo Section */
.logo-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.logo-image {
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, var(--royal-blue) 0%, var(--navy-blue) 100%);
  padding: 8px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-name {
  font-family: var(--font-heading);
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--gold);
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  line-height: 1.1;
}

.brand-tagline {
  font-size: 1rem;
  color: var(--cream);
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.brand-accent {
  font-size: 0.8rem;
  color: var(--light-gold);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

/* Primary Navigation */
.primary-nav {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.nav-menu {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 8px;
}

.nav-item {
  position: relative;
}

.nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  color: var(--cream);
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 184, 28, 0.1), transparent);
  transition: left 0.5s ease;
}

.nav-link:hover {
  color: var(--gold);
  background: rgba(255, 184, 28, 0.1);
  transform: translateY(-2px);
}

.nav-link:hover::before {
  left: 100%;
}

.nav-link.router-link-exact-active {
  color: var(--gold);
  background: rgba(255, 184, 28, 0.15);
  border-bottom: 2px solid var(--gold);
}

/* Mobile Toggle */
.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.hamburger-line {
  width: 24px;
  height: 3px;
  background: var(--cream);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.mobile-toggle:hover .hamburger-line {
  background: var(--gold);
}

.mobile-toggle.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.mobile-toggle.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-toggle.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Mobile Navigation */
.mobile-nav-menu {
  display: none;
  background: var(--navy-blue);
  border-top: 1px solid rgba(255, 184, 28, 0.2);
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;
}

.mobile-nav-menu.open {
  max-height: 400px;
}

.mobile-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-nav-list li {
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
}

.mobile-nav-link {
  display: block;
  padding: 16px 24px;
  color: var(--cream);
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-exact-active {
  background: rgba(255, 184, 28, 0.1);
  color: var(--gold);
  padding-left: 32px;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .brand-name {
    font-size: 1.8rem;
  }
  
  .brand-tagline {
    font-size: 0.9rem;
  }
  
  .nav-link {
    padding: 10px 12px;
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 0 16px;
  }
  
  .top-nav-bar {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  
  .main-header-content {
    padding: 16px 0;
    gap: 20px;
  }
  
  .logo-image {
    width: 60px;
    height: 60px;
    padding: 6px;
  }
  
  .brand-name {
    font-size: 1.5rem;
  }
  
  .brand-tagline {
    font-size: 0.8rem;
  }
  
  .brand-accent {
    font-size: 0.7rem;
  }
  
  .nav-menu {
    display: none;
  }
  
  .mobile-toggle {
    display: flex;
  }
  
  .mobile-nav-menu {
    display: block;
  }
}

/* Extra mobile optimization for very small screens */
@media (max-width: 640px) {
  .top-nav-bar {
    display: none; /* Hide contact info on small screens */
  }
  
  .main-header-content {
    padding: 12px 0;
  }
  
  .logo-image {
    width: 50px;
    height: 50px;
    padding: 5px;
  }
  
  .brand-name {
    font-size: 1.3rem;
  }
  
  .brand-tagline {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .logo-section {
    gap: 16px;
  }
  
  .logo-image {
    width: 45px;
    height: 45px;
    padding: 4px;
  }
  
  .brand-name {
    font-size: 1.2rem;
  }
  
  .contact-info {
    flex-direction: column;
    gap: 8px;
  }
  
  .contact-item span {
    font-size: 0.8rem;
  }
}
</style>
